/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Domain types for Audit Logs. These mirror the api-insights `/audits` contract exactly — the
 * backend already maps goldeye's snake_case into this shape, so nothing here should re-map names.
 */

export const CHANGE_TYPES = ['create', 'update', 'delete', 'other'] as const
export type ChangeType = (typeof CHANGE_TYPES)[number]

export const ENTITY_TYPES = [
  'alertConfiguration',
  'virtualSetPoint',
  'geofence',
  'asset',
  'alertSubscription',
  'userAdministration',
  'other',
] as const
export type EntityType = (typeof ENTITY_TYPES)[number]

export const APPLICATIONS = ['refresh', 'octopus', 'bonito', 'other'] as const
export type Application = (typeof APPLICATIONS)[number]

/**
 * One field-level change.
 *
 * `o` is the operation, `p` a dot-separated path into the entity, `v` the value added or removed
 * (for a replace, the value *before* the change) and `r` the replacement.
 *
 * `o` arrives as the numeric enum (0 Add, 1 Replace, 2 Remove); older records carry the string
 * names, so both are handled — see `normalizeDiff`.
 */
export interface AuditDiffEntry {
  o: number | string
  p: string
  v?: unknown
  r?: unknown
}

/** A row in the list. Carries `changeCount` and a server-generated `summary`, never the diff. */
export interface AuditRecord {
  id: string
  loggedAt: string
  application: string
  changeType: string
  entityType: string
  entityId: string
  entityName: string
  companyId: number
  /** Null when the name could not be resolved; show `companyId`. */
  companyName: string | null
  userId: string
  /** Null when the identifier is not a Fresh user or the lookup failed; show `userId`. */
  userName: string | null
  userIpAddress: string
  changeCount: number
  summary: string
}

/** One field, whole, as it stood on each side of the change. `null` where it did not exist. */
export interface AuditSnapshot {
  old: unknown
  new: unknown
}

/**
 * Whole-field context the diff cannot supply on its own.
 *
 * A diff walks paths, so a polygon edit arrives as `geofencepoints.4.latitude` — enough to say what
 * changed, not enough to draw the shape it changed. Where the producing application asks for it, the
 * audit store keeps the entire field from both sides alongside the diff.
 *
 * Only present on records written after that was introduced, and only for a field that both was
 * asked for and actually changed. Everything reading this must work when it is absent.
 */
export interface AuditMetadata {
  /** Keyed by the entity's own field name, e.g. `geofencepoints`. */
  snapshots: Record<string, AuditSnapshot>
  /** Fields that changed but were too large for the store to keep. */
  truncated?: string[]
}

/** The detail view: a row plus its full diff, and whole-field snapshots where the record has them. */
export interface AuditDetail extends AuditRecord {
  diff: AuditDiffEntry[]
  /** Absent on most records — see `AuditMetadata`. */
  metadata?: AuditMetadata
}

export interface AuditListResponse {
  data: AuditRecord[]
  /** Only present when the request opted in; the summary total comes from facets instead. */
  count?: number
  hasMore: boolean
  nextCursor: string | null
  namesUnavailable?: boolean
}

export interface AuditFacets {
  total: number
  byChangeType: Record<string, number>
  byEntityType: Record<string, number>
  byApplication: Record<string, number>
  users: { userId: string; userName: string | null; count: number }[]
  companies: { companyId: number; companyName: string | null }[]
  range: { earliest: string | null; latest: string | null }
}

export type TimePreset = '24h' | '7d' | '30d' | '90d' | 'custom'

/**
 * The complete filter state. Every field here is applied by the API over the whole history — none
 * is evaluated client-side over loaded rows, which would make results depend on scroll depth.
 */
export interface AuditFilters {
  companyIds: number[]
  time: TimePreset
  /** Absolute bounds, set only when `time` is 'custom'. ISO-8601. */
  from?: string
  to?: string
  changeTypes: ChangeType[]
  entityTypes: EntityType[]
  userIds: string[]
  /** Set by "see all changes to this entity"; a mode rather than an ordinary filter. */
  entityId?: string
  search: string
}
